import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Loader2, Pencil, Plus, RotateCcw, X } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { usePoll } from "@/lib/hooks";

const FixRow = ({ it, canEdit, onPatch, onDelete }) => {
  const [edit, setEdit] = useState(null);
  const save = () => { if (edit?.trim()) onPatch(it, { correction: edit.trim() }); setEdit(null); };
  return (
    <li className={`flex items-start gap-2.5 rounded-xl px-2.5 py-1.5 text-sm transition-colors ${it.ticked ? "bg-white/60" : "opacity-60"}`} data-testid={`fix-item-${it.id}`}>
      <button type="button" disabled={!canEdit} onClick={() => onPatch(it, { ticked: !it.ticked })} aria-pressed={it.ticked} data-testid={`fix-toggle-${it.id}`}
        className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-xl border ${it.ticked ? "border-[var(--charcoal)] bg-[var(--charcoal)] text-[var(--bone)]" : "border-[var(--line)] bg-transparent"} disabled:cursor-default`}>
        {it.ticked && <Check size={13} />}
      </button>
      <div className="min-w-0 flex-1 leading-snug">
        {edit !== null ? (
          <form className="mt-1 flex gap-1.5" onSubmit={(e) => { e.preventDefault(); save(); }}>
            <input autoFocus maxLength={140} value={edit} onChange={(e) => setEdit(e.target.value)} className="field !min-h-[36px] flex-1 !py-1 text-sm" data-testid={`fix-edit-input-${it.id}`} />
            <button className="btn-quiet !min-h-[36px] text-xs" data-testid={`fix-edit-save-${it.id}`}>Save</button>
          </form>
        ) : (
          <span className={`first-letter:uppercase block ${it.ticked ? "font-medium" : "line-through"}`} data-testid={`fix-correction-${it.id}`}>{it.correction}</span>
        )}
        {it.problem && <span className="first-letter:uppercase mt-0.5 block text-xs text-[var(--stone-dark)]">{it.ticked ? "Fixes: " : ""}{it.problem}{it.ticked ? "" : " · left as is"}</span>}
        {!it.ticked && !it.problem && <span className="text-xs text-[var(--stone-dark)]">{it.relocate ? "· stays in place" : "· left as is"}</span>}
        {it.note && <span className="mt-0.5 block text-[0.68rem] text-[#B42318]/80" data-testid={`fix-note-${it.id}`}>{it.note}</span>}
      </div>
      {canEdit && edit === null && (
        <span className="flex shrink-0 gap-1">
          <button type="button" className="p-1 text-[var(--stone-dark)] hover:text-[var(--charcoal)]" onClick={() => setEdit(it.correction)} aria-label="Edit fix" data-testid={`fix-edit-${it.id}`}><Pencil size={13} /></button>
          {it.custom && <button type="button" className="p-1 text-[var(--stone-dark)] hover:text-[var(--charcoal)]" onClick={() => onDelete(it)} aria-label="Remove fix" data-testid={`fix-remove-${it.id}`}><X size={14} /></button>}
        </span>
      )}
    </li>
  );
};

export const FixChecklist = ({ roomId, compact = false }) => {
  const [ck, setCk] = useState(null);
  const [text, setText] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get(`/rooms/${roomId}/fixes`).then((r) => setCk(r.data)).catch(() => {}), [roomId]);
  useEffect(() => { load(); }, [load]);
  usePoll(load, ck?.status === "detecting", 2500);
  const run = async (fn) => { setBusy(true); try { const { data } = await fn(); if (data?.items) setCk((c) => ({ ...c, ...data, priorities: c?.priorities })); } catch (e) { toast(errMsg(e, "Couldn't update the checklist.")); } setBusy(false); };
  const patch = (it, body) => run(() => api.patch(`/rooms/${roomId}/fixes/${it.id}`, body));
  const remove = (it) => run(() => api.delete(`/rooms/${roomId}/fixes/${it.id}`));
  const add = (t, source = "custom") => run(async () => { const r = await api.post(`/rooms/${roomId}/fixes`, { text: t, source }); setText(null); load(); return r; });
  const retry = () => run(() => api.post(`/rooms/${roomId}/fixes/detect`));
  if (!ck) return null;
  const ticked = ck.items.filter((i) => i.ticked).length;
  if (compact && !ck.canEdit) {
    return (
      <details className="rounded-xl bg-[var(--bone-2)] px-4 py-3 text-sm" data-testid={`fix-checklist-${roomId}`}>
        <summary className="cursor-pointer list-none"><span className="eyebrow">Fixes to include</span> <span className="ml-1 text-xs text-[var(--stone-dark)]" data-testid="fix-count">{ticked} of {ck.items.length} ticked · view only</span></summary>
        <ul className="mt-2 grid gap-1 text-xs">{ck.items.map((it) => <li key={it.id} className={it.ticked ? "" : "line-through opacity-60"}>{it.ticked ? "✓" : "○"} {it.correction}</li>)}</ul>
      </details>
    );
  }
  return (
    <section className={`rounded-xl bg-[var(--bone-2)] ${compact ? "p-4" : "p-4 sm:p-5"}`} data-testid={`fix-checklist-${roomId}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="eyebrow">Fixes to include</div>
        <span className="text-xs text-[var(--stone-dark)]" data-testid="fix-count">{ticked} of {ck.items.length} ticked{ck.canEdit ? "" : " · view only"}</span>
      </div>
      {ck.status === "detecting" && <p className="mt-2 flex items-center gap-2 text-sm" data-testid="fix-detecting"><Loader2 size={14} className="animate-spin" /> Finding fixes…</p>}
      {ck.status === "failed" && (
        <p className="mt-2 flex flex-wrap items-center gap-2 text-sm text-[#B42318]" data-testid="fix-detect-failed">
          {ck.message} {ck.canEdit && <button className="btn-quiet !min-h-[32px] text-xs" onClick={retry} data-testid="fix-detect-retry"><RotateCcw size={12} /> Try again</button>}
        </p>
      )}
      {ck.items.length > 0 && <ul className="mt-3 grid gap-1.5">{ck.items.map((it) => <FixRow key={it.id} it={it} canEdit={ck.canEdit} onPatch={patch} onDelete={remove} />)}</ul>}
      {ck.canEdit && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {text !== null ? (
            <form className="flex w-full gap-1.5" onSubmit={(e) => { e.preventDefault(); if (text.trim()) add(text.trim()); }}>
              <input autoFocus maxLength={140} value={text} onChange={(e) => setText(e.target.value)} placeholder="e.g. Add under-cabinet lighting" className="field !min-h-[40px] flex-1 text-sm" data-testid="fix-add-input" />
              <button className="btn-primary !min-h-[40px] !px-4 text-sm" disabled={busy || !text.trim()} data-testid="fix-add-save">Add</button>
              <button type="button" className="btn-quiet" onClick={() => setText(null)} aria-label="Cancel"><X size={15} /></button>
            </form>
          ) : (
            <button className="btn-quiet !min-h-[36px] text-sm" disabled={ck.full} onClick={() => setText("")} data-testid="fix-add-button"><Plus size={14} /> Add a fix</button>
          )}
          {ck.full && <span className="text-xs text-[var(--stone-dark)]" data-testid="fix-limit-message">{ck.limitMessage}</span>}
          {!ck.full && ck.priorities?.length > 0 && text === null && (
            <select className="min-h-[36px] max-w-[15rem] rounded-full border border-[var(--line)] bg-transparent px-3 text-sm" value="" onChange={(e) => { const p = ck.priorities.find((x) => x.id === e.target.value); if (p) add(p.text.slice(0, 140), "priority"); }} data-testid="fix-add-from-priorities">
              <option value="">Add from priorities…</option>
              {ck.priorities.map((p) => <option key={p.id} value={p.id}>{p.text.slice(0, 60)}</option>)}
            </select>
          )}
        </div>
      )}
    </section>
  );
};
